/**
 * HYPSEYA — Clinical Compliance & Data Governance
 * For clinical partners: GDPR Art. 9, DPA, SaMD positioning,
 * data retention, storage, and patient rights.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const TEAL = '#1E6B68';
const DARK = '#1E3A39';
const CREAM = 'rgba(247,243,236,0.97)';
const BORDER = 'rgba(30,107,104,0.12)';

const SECTIONS = [
  {
    id: 'positioning',
    title: 'Platform Classification',
    badge: 'SaMD · MDR',
    color: '#2D8B87',
    items: [
      {
        label: 'Wellness tool — not a medical device',
        text: 'HYPSEYA is an emotional regulation and wellness platform. It is not classified as a medical device under EU MDR 2017/745 or FDA 21 CFR Part 880. It does not diagnose, treat, or cure any clinical condition.',
      },
      {
        label: 'Clinical measurement — informational only',
        text: 'PHQ-9 and GAD-7 scores are self-reported standardised questionnaires provided as informational tools. They do not constitute clinical diagnoses. All clinical judgement remains with the licensed practitioner.',
      },
      {
        label: 'Intended use',
        text: 'HYPSEYA is intended as a structured self-management support tool for emotional regulation, used independently or as an adjunct to licensed psychotherapy. It is not intended for use in emergency or acute psychiatric settings.',
      },
      {
        label: 'Risk classification',
        text: 'As a wellness application without diagnostic claims, HYPSEYA is currently positioned below the threshold for SaMD regulation in the EU. Clinical partners should conduct their own risk assessment when integrating the platform into care pathways.',
      },
    ],
  },
  {
    id: 'gdpr-art9',
    title: 'GDPR Article 9 — Health Data',
    badge: 'GDPR · Art. 9',
    color: '#7B68A0',
    items: [
      {
        label: 'Special category personal data',
        text: 'Emotional regulation data, PHQ-9/GAD-7 scores, journal entries, and somatic data collected by HYPSEYA may constitute health data under Article 9 of the EU GDPR (Regulamento (UE) 2016/679). HYPSEYA processes this data under Article 9(2)(a) — explicit consent of the data subject.',
      },
      {
        label: 'Lawful basis',
        text: 'Processing is based on: (a) explicit and informed consent (Art. 9(2)(a)) collected at onboarding; (b) legitimate interest in providing the contracted service (Art. 6(1)(b)); and (c) for clinical partners, contractual necessity under a Data Processing Agreement.',
      },
      {
        label: 'Data minimisation',
        text: 'We collect only the minimum data necessary to provide the service. No biometric identifiers, diagnostic labels, or third-party medical records are ingested by the platform.',
      },
      {
        label: 'Privacy by design',
        text: 'PHQ-9 item 9 (suicidal ideation) flags are stored ephemerally in memory during session processing and only the numeric score is persisted. Journal entries are encrypted at rest. No AI model is trained on user data.',
      },
    ],
  },
  {
    id: 'dpa',
    title: 'Data Processing Agreement (DPA)',
    badge: 'Clinical Partners',
    color: '#B8906A',
    items: [
      {
        label: 'Requirement for clinical partners',
        text: 'Clinics, hospitals, and practitioners who integrate HYPSEYA into patient care must execute a Data Processing Agreement (DPA) with HYPSEYA Lda. prior to any data sharing. Contact clinical@hypseya.com to initiate this process.',
      },
      {
        label: 'Therapist-patient data access',
        text: 'Therapist access to patient data requires explicit mutual consent. Patients can grant or revoke therapist access at any time. Therapists cannot access any data unless the patient has accepted the connection invitation.',
      },
      {
        label: 'Sub-processor disclosure',
        text: 'HYPSEYA uses the following sub-processors: MongoDB Atlas (EU-West data region, Ireland); Auth0/Okta (authentication); OpenAI (AI-generated content only — no raw user data is sent, only anonymised summaries); ElevenLabs (voice synthesis); Stripe (payments). All sub-processors are contractually bound to GDPR standards.',
      },
      {
        label: 'Data transfer',
        text: 'All primary data storage occurs within the EU (MongoDB Atlas, eu-west-1, Ireland). AI processing via OpenAI may involve temporary transfer to the US under Standard Contractual Clauses (SCCs) per Art. 46 GDPR. No raw journal content or PHQ-9 answers are transmitted to AI services.',
      },
    ],
  },
  {
    id: 'retention',
    title: 'Data Retention & Deletion',
    badge: 'Retention Policy',
    color: '#4A7B9D',
    items: [
      {
        label: 'Active accounts',
        text: 'Data is retained for the duration of the account and for 24 months after the last login, unless earlier deletion is requested.',
      },
      {
        label: 'Clinical assessments (PHQ-9/GAD-7)',
        text: 'Assessment history is retained for 36 months from date of completion to support longitudinal clinical tracking. Patients may request earlier deletion via the in-app privacy settings.',
      },
      {
        label: 'Therapist connections',
        text: 'After a therapist connection is revoked, the therapist loses all access to new data immediately. Audit logs of the connection period are retained for 12 months for compliance purposes.',
      },
      {
        label: 'Right to erasure (Art. 17 GDPR)',
        text: 'Users may request complete account deletion at any time via privacy@hypseya.com. Deletion is processed within 30 days. Anonymised aggregate statistics (no personal identifiers) may be retained for research purposes.',
      },
    ],
  },
  {
    id: 'patient-rights',
    title: 'Patient Rights',
    badge: 'Art. 12–22 GDPR',
    color: TEAL,
    items: [
      {
        label: 'Right to access (Art. 15)',
        text: 'Users may request a full export of their personal data at any time via privacy@hypseya.com. We provide this in machine-readable JSON format within 30 days.',
      },
      {
        label: 'Right to rectification (Art. 16)',
        text: 'Users may correct inaccurate personal data directly in the app (profile settings) or by contacting us.',
      },
      {
        label: 'Right to portability (Art. 20)',
        text: 'All ERI scores, session history, PHQ-9/GAD-7 results, and journal entries are exportable in structured JSON format.',
      },
      {
        label: 'Right to object to processing (Art. 21)',
        text: 'Users may object to any processing at any time. Objection will result in immediate suspension of data processing and account deactivation pending deletion.',
      },
      {
        label: 'Supervisory authority',
        text: 'Users have the right to lodge a complaint with their national supervisory authority. In Portugal: Comissão Nacional de Proteção de Dados (CNPD) — www.cnpd.pt.',
      },
    ],
  },
  {
    id: 'security',
    title: 'Security Measures',
    badge: 'Technical · Organisational',
    color: '#4AACA8',
    items: [
      {
        label: 'Encryption',
        text: 'All data is encrypted in transit (TLS 1.3) and at rest (AES-256). Passwords are never stored — authentication is delegated to Auth0 (SOC 2 Type II, ISO 27001 certified).',
      },
      {
        label: 'Access control',
        text: 'Role-based access control (RBAC) enforces strict separation between user, therapist, and admin data. Therapist data access requires API token validation on every request.',
      },
      {
        label: 'Incident response',
        text: 'In the event of a personal data breach, affected users and the relevant supervisory authority will be notified within 72 hours as required by Art. 33 GDPR.',
      },
      {
        label: 'Penetration testing',
        text: 'Annual third-party security audits are planned as part of the clinical partnership programme. Clinical partners may request the most recent audit report under NDA.',
      },
    ],
  },
];

function Section({ section, isOpen, onToggle }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 18, marginBottom: 10,
      boxShadow: '0 2px 14px rgba(30,107,104,0.05)',
      border: isOpen ? `1.5px solid ${section.color}40` : `1px solid ${BORDER}`,
      overflow: 'hidden',
      transition: 'border-color 0.2s ease',
    }}>
      <button onClick={onToggle} style={{
        width: '100%', padding: '18px 20px', background: 'none', border: 'none',
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
            <span style={{ fontSize: '0.9rem', fontWeight: '700', color: DARK }}>{section.title}</span>
            <span style={{ fontSize: '0.6rem', fontWeight: '700', letterSpacing: '0.07em', textTransform: 'uppercase', background: `${section.color}14`, color: section.color, padding: '2px 8px', borderRadius: 8 }}>
              {section.badge}
            </span>
          </div>
        </div>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
          <path d="M2 4l5 5 5-5" stroke="#8FA3A0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {isOpen && (
        <div style={{ padding: '0 20px 20px', borderTop: `1px solid ${section.color}20` }}>
          {section.items.map((item, i) => (
            <div key={i} style={{ paddingTop: 14, borderTop: i > 0 ? '1px solid rgba(184,211,207,0.25)' : 'none', marginTop: i > 0 ? 0 : 14 }}>
              <p style={{ margin: '0 0 5px', fontSize: '0.82rem', fontWeight: '700', color: section.color }}>{item.label}</p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#5A7A78', lineHeight: 1.65 }}>{item.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ClinicalCompliance() {
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState('positioning');

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 55%, rgba(184,211,207,0.15) 100%)',
      padding: '20px 20px 100px',
      maxWidth: 680, margin: '0 auto',
    }}>
      {/* Back */}
      <button onClick={() => navigate(-1)} style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(184,211,207,0.4)', borderRadius: 20, padding: '8px 16px', cursor: 'pointer', fontSize: '0.82rem', color: '#8FA3A0', marginBottom: 24 }}>
        ← Back
      </button>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: TEAL, marginBottom: 6 }}>
          Clinical Partners · Data Governance
        </div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.7rem', color: DARK, margin: '0 0 8px', fontWeight: '500' }}>
          Compliance & Privacy
        </h1>
        <p style={{ margin: 0, color: '#8FA3A0', fontSize: '0.85rem', lineHeight: 1.6 }}>
          GDPR Article 9 · Data Processing Agreement · SaMD Classification · Patient Rights
        </p>
      </div>

      {/* Key signals */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 24 }}>
        {[
          { label: 'Data region', value: 'EU-West', sub: 'Ireland · Atlas' },
          { label: 'Auth', value: 'Auth0', sub: 'SOC 2 · ISO 27001' },
          { label: 'Encryption', value: 'AES-256', sub: 'TLS 1.3' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', borderRadius: 14, padding: '14px 10px', textAlign: 'center', boxShadow: '0 2px 10px rgba(30,107,104,0.05)' }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.1rem', fontWeight: '700', color: TEAL }}>{s.value}</div>
            <div style={{ fontSize: '0.6rem', color: '#AAB8B6', fontWeight: '600', letterSpacing: '0.04em', marginTop: 2 }}>{s.sub}</div>
            <div style={{ fontSize: '0.6rem', color: '#C5D4D2', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 1 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Sections */}
      {SECTIONS.map(section => (
        <Section
          key={section.id}
          section={section}
          isOpen={openSection === section.id}
          onToggle={() => setOpenSection(openSection === section.id ? null : section.id)}
        />
      ))}

      {/* Contact */}
      <div style={{ marginTop: 20, padding: '18px 20px', background: 'rgba(30,107,104,0.05)', border: '1px solid rgba(30,107,104,0.12)', borderRadius: 16 }}>
        <p style={{ margin: '0 0 6px', fontSize: '0.82rem', fontWeight: '700', color: TEAL }}>Clinical Partnership Enquiries</p>
        <p style={{ margin: 0, fontSize: '0.78rem', color: '#6B8B87', lineHeight: 1.6 }}>
          For DPAs, partnership discussions, or audit requests: <strong>clinical@hypseya.com</strong>
          <br />For privacy and data subject requests: <strong>privacy@hypseya.com</strong>
          <br />Last updated: May 2026 · Version 1.0
        </p>
      </div>
    </div>
  );
}
